import React, { useState, useMemo } from 'react';
import { useAppStateApartmentCompareListStore } from '../../../../common/store/AppStateStore.zustand';
import { useApartmentCompareAPI } from '../../api/ApartmentCompare.api';
import { DEFAULT_APARTMENT_COMPARE } from '../../type/ApartmentCompare.type';
import lawdCodeData from '../../../../common/data/lawdCode.json';

// 서울 열린데이터광장 API → 서울특별시만 지원
const SUPPORTED_SIDO = '서울특별시';

export interface AddApartmentModalData {
  isOpen: boolean;
  onClose: () => void;
}

type Step = 1 | 2 | 3;

const AddApartmentModal = ({ isOpen, onClose }: AddApartmentModalData) => {
  const { insertApartmentCompare, getApartmentCompareList, searchApartments } = useApartmentCompareAPI();
  const { setApartmentCompareList } = useAppStateApartmentCompareListStore();

  const [step, setStep] = useState<Step>(1);
  const [selectedSido, setSelectedSido] = useState('');
  const [selectedSigungu, setSelectedSigungu] = useState('');
  const [lawdCd, setLawdCd] = useState('');
  const [aptNameKeyword, setAptNameKeyword] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<{ aptName: string; excluUseAr: number }[]>([]);
  const [searchError, setSearchError] = useState('');
  const [selectedApt, setSelectedApt] = useState<{ aptName: string; excluUseAr: number } | undefined>();
  const [displayName, setDisplayName] = useState('');

  const sidoList = useMemo(() => [SUPPORTED_SIDO], []);

  const sigunguList = useMemo(
    () => lawdCodeData.filter((d) => d.sido === selectedSido).map((d) => ({ sigungu: d.sigungu, lawdCd: d.lawdCd })),
    [selectedSido],
  );

  const handleSidoChange = (sido: string) => {
    setSelectedSido(sido);
    setSelectedSigungu('');
    setLawdCd('');
  };

  const handleSigunguChange = (sigungu: string) => {
    setSelectedSigungu(sigungu);
    const found = sigunguList.find((s) => s.sigungu === sigungu);
    setLawdCd(found?.lawdCd || '');
  };

  const handleSearch = async () => {
    if (!aptNameKeyword.trim()) return;
    setIsSearching(true);
    setSearchError('');
    setSearchResults([]);
    setSelectedApt(undefined);

    try {
      // 직전 연도 기준으로 검색 (당해연도 데이터는 아직 적을 수 있음)
      const searchYear = String(new Date().getFullYear() - 1);
      const results = await searchApartments(lawdCd, aptNameKeyword.trim(), searchYear);
      if (results.length === 0) {
        setSearchError('검색 결과가 없습니다. 다른 이름이나 지역을 시도해보세요.');
      } else {
        setSearchResults(results);
      }
    } catch {
      setSearchError('검색 중 오류가 발생했습니다. API 키와 네트워크를 확인해주세요.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectApt = (apt: { aptName: string; excluUseAr: number }) => {
    setSelectedApt(apt);
    setDisplayName(`${apt.aptName} ${apt.excluUseAr}㎡`);
  };

  const handleSave = async () => {
    if (!selectedApt || !displayName.trim()) return;
    await insertApartmentCompare({
      ...DEFAULT_APARTMENT_COMPARE,
      name: displayName.trim(),
      lawdCd,
      sidoName: selectedSido,
      sigunguName: selectedSigungu,
      aptName: selectedApt.aptName,
      excluUseAr: selectedApt.excluUseAr,
    });
    const { data } = await getApartmentCompareList();
    setApartmentCompareList(data);
    handleClose();
  };

  const handleClose = () => {
    setStep(1);
    setSelectedSido('');
    setSelectedSigungu('');
    setLawdCd('');
    setAptNameKeyword('');
    setSearchResults([]);
    setSearchError('');
    setSelectedApt(undefined);
    setDisplayName('');
    onClose();
  };

  if (!isOpen) return <></>;

  return (
    <div className="fixed h-screen inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-lg w-[560px] shadow-xl">
        {/* 헤더 */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">아파트 추가</h2>
              <div className="flex items-center space-x-2 mt-2">
                {([1, 2, 3] as Step[]).map((s) => (
                  <div key={s} className="flex items-center">
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        step === s ? 'bg-indigo-600 text-white' : step > s ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {step > s ? <i className="fas fa-check text-xs"></i> : s}
                    </div>
                    {s < 3 && <div className={`w-8 h-0.5 mx-1 ${step > s ? 'bg-green-500' : 'bg-gray-200'}`}></div>}
                  </div>
                ))}
                <span className="text-sm text-gray-500 ml-2">
                  {step === 1 && '지역 선택'}
                  {step === 2 && '아파트 검색'}
                  {step === 3 && '저장'}
                </span>
              </div>
            </div>
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        {/* 본문 */}
        <div className="p-6">
          {/* Step 1: 지역 선택 */}
          {step === 1 && (
            <div className="space-y-5">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                <i className="fas fa-info-circle mr-2"></i>
                서울시 열린데이터광장 API를 사용합니다. 현재 <strong>서울특별시</strong>만 지원됩니다.
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">시/도</label>
                <select
                  value={selectedSido}
                  onChange={(e) => handleSidoChange(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-indigo-500 transition-colors"
                >
                  <option value="">시/도 선택</option>
                  {sidoList.map((sido) => (
                    <option key={sido} value={sido}>{sido}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">시/군/구</label>
                <select
                  value={selectedSigungu}
                  onChange={(e) => handleSigunguChange(e.target.value)}
                  disabled={!selectedSido}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-indigo-500 transition-colors disabled:bg-gray-50 disabled:text-gray-400"
                >
                  <option value="">시/군/구 선택</option>
                  {sigunguList.map((s) => (
                    <option key={s.lawdCd} value={s.sigungu}>{s.sigungu}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Step 2: 아파트 검색 */}
          {step === 2 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  아파트 이름 검색 ({selectedSido} {selectedSigungu})
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={aptNameKeyword}
                    onChange={(e) => setAptNameKeyword(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-indigo-500 transition-colors"
                    placeholder="예: 래미안, 힐스테이트"
                  />
                  <button
                    onClick={handleSearch}
                    disabled={isSearching || !aptNameKeyword.trim()}
                    className="px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                  >
                    {isSearching ? <i className="fas fa-spinner fa-spin"></i> : '검색'}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">최근 3개월 실거래 기준으로 검색합니다</p>
              </div>

              {searchError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">
                  <i className="fas fa-exclamation-circle mr-2"></i>{searchError}
                </div>
              )}

              {searchResults.length > 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden max-h-56 overflow-y-auto">
                  {searchResults.map((apt) => {
                    const key = `${apt.aptName}|${apt.excluUseAr}`;
                    const isSelected = selectedApt?.aptName === apt.aptName && selectedApt?.excluUseAr === apt.excluUseAr;
                    return (
                      <button
                        key={key}
                        onClick={() => handleSelectApt(apt)}
                        className={`w-full text-left px-4 py-3 border-b border-gray-100 last:border-b-0 hover:bg-indigo-50 transition-colors flex justify-between items-center ${
                          isSelected ? 'bg-indigo-50 border-l-4 border-l-indigo-500' : ''
                        }`}
                      >
                        <span className="font-medium text-gray-800">{apt.aptName}</span>
                        <span className="text-sm text-gray-500">{apt.excluUseAr}㎡</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Step 3: 저장 */}
          {step === 3 && selectedApt && (
            <div className="space-y-5">
              <div className="bg-indigo-50 rounded-lg p-4">
                <p className="text-sm text-indigo-700 font-medium">선택된 아파트</p>
                <p className="text-gray-800 font-semibold mt-1">{selectedApt.aptName}</p>
                <p className="text-sm text-gray-600">{selectedSido} {selectedSigungu} · {selectedApt.excluUseAr}㎡</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">표시명</label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-indigo-500 transition-colors"
                  placeholder="카드에 표시될 이름"
                />
              </div>
            </div>
          )}
        </div>

        {/* 푸터 버튼 */}
        <div className="p-6 border-t border-gray-200 flex justify-between">
          <button
            onClick={() => (step === 1 ? handleClose() : setStep((step - 1) as Step))}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 !rounded-button"
          >
            {step === 1 ? '취소' : '이전'}
          </button>
          {step === 1 && (
            <button
              disabled={!selectedSigungu}
              onClick={() => setStep(2)}
              className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md text-sm !rounded-button"
            >
              다음
            </button>
          )}
          {step === 2 && (
            <button
              disabled={!selectedApt}
              onClick={() => setStep(3)}
              className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md text-sm !rounded-button"
            >
              다음
            </button>
          )}
          {step === 3 && (
            <button
              disabled={!displayName.trim()}
              onClick={handleSave}
              className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md text-sm !rounded-button"
            >
              저장
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddApartmentModal;
